import { useCallback, useEffect, useState } from 'react'
import type { Site } from '@shared/types'
import { get, patch, post } from '../../lib/api'
import { getCurrentFix } from '../../lib/geo'
import { useToast } from '../../state/toast'
import {
  Button, ErrorNote, Field, NumberInput, SectionHeading, Sheet, Spinner,
} from '../../components/ui'

// Brand yellows first, then distinguishable site colours.
const SWATCHES = ['#FAA41B', '#F4C215', '#4A5A6A', '#2C7A4B', '#B3241A', '#5B7553', '#7A5CA8', '#6B6560']

export default function AdminSites() {
  const toast = useToast()
  const [sites, setSites] = useState<Site[] | null>(null)
  const [editing, setEditing] = useState<Partial<Site> | null>(null)

  const load = useCallback(async () => {
    setSites((await get<{ sites: Site[] }>('/api/admin/sites')).sites)
  }, [])

  useEffect(() => {
    void load().catch(() => setSites([]))
  }, [load])

  if (!sites) return <div className="py-16"><Spinner /></div>

  return (
    <div className="space-y-4">
      <SectionHeading
        action={
          <Button
            className="min-h-9 px-3 text-xs"
            onClick={() => setEditing({ radiusM: 150, color: SWATCHES[0], active: true })}
          >
            Add site
          </Button>
        }
      >
        {sites.filter((s) => s.active).length} active sites
      </SectionHeading>

      <div className="space-y-2">
        {sites.map((site) => (
          <button
            key={site.id}
            type="button"
            onClick={() => setEditing(site)}
            className={`card flex w-full items-start gap-3 p-3 text-left ${site.active ? '' : 'opacity-55'}`}
          >
            <span
              className="mt-1 size-3 shrink-0 rounded-full"
              style={{ background: site.color }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold">{site.name}</p>
              {site.address && <p className="truncate text-xs text-graphite-2">{site.address}</p>}
              <p className="font-mono text-[10px] tracking-wider text-graphite-2 uppercase">
                {site.lat != null && site.lng != null
                  ? `${site.lat.toFixed(5)}, ${site.lng.toFixed(5)} · ${site.radiusM} m fence`
                  : 'No location — no arrival reminders'}
              </p>
              {site.createdByName && (
                <p className="text-xs text-graphite-2">Added by {site.createdByName}</p>
              )}
            </div>
          </button>
        ))}
      </div>

      {editing && (
        <SiteSheet
          site={editing}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            setEditing(null)
            toast.success(message)
            await load()
          }}
        />
      )}
    </div>
  )
}

function SiteSheet({
  site, onClose, onSaved,
}: {
  site: Partial<Site>
  onClose: () => void
  onSaved: (message: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<Partial<Site>>(site)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [locating, setLocating] = useState(false)
  const isNew = !site.id

  const useMyLocation = async () => {
    setLocating(true)
    try {
      const fix = await getCurrentFix()
      setDraft((current) => ({ ...current, lat: fix.lat, lng: fix.lng }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read your location')
    } finally {
      setLocating(false)
    }
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    const payload = {
      name: draft.name,
      address: draft.address,
      lat: draft.lat ?? null,
      lng: draft.lng ?? null,
      radiusM: draft.radiusM,
      color: draft.color,
      notes: draft.notes,
      active: draft.active,
    }
    try {
      if (isNew) await post('/api/admin/sites', payload)
      else await patch(`/api/admin/sites/${site.id}`, payload)
      await onSaved(isNew ? 'Site added' : 'Site updated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the site')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'New site' : draft.name ?? 'Site'}
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void save()}>
          Save site
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <input
            className="field"
            value={draft.name ?? ''}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Riverbend Townhomes"
          />
        </Field>

        <Field label="Address">
          <input
            className="field"
            value={draft.address ?? ''}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
          />
        </Field>

        <div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude">
              <NumberInput
                className="font-mono"
                decimal
                value={draft.lat}
                onChange={(n) => setDraft({ ...draft, lat: n })}
              />
            </Field>
            <Field label="Longitude">
              <NumberInput
                className="font-mono"
                decimal
                value={draft.lng}
                onChange={(n) => setDraft({ ...draft, lng: n })}
              />
            </Field>
          </div>
          <Button className="mt-2 w-full" loading={locating} onClick={() => void useMyLocation()}>
            Use my current location
          </Button>
          <p className="mt-1 text-xs text-graphite-2">
            Without coordinates the site still works for clocking in — it just will not trigger
            arrival reminders.
          </p>
        </div>

        <Field
          label={`Geofence radius — ${draft.radiusM ?? 150} m`}
          hint="Large enough to cover the whole property. 150 m suits most residential lots."
        >
          <input
            type="range"
            min={25}
            max={1000}
            step={25}
            className="w-full accent-[var(--wm-brand-deep)]"
            value={draft.radiusM ?? 150}
            onChange={(e) => setDraft({ ...draft, radiusM: Number(e.target.value) })}
          />
        </Field>

        <Field label="Colour">
          <div className="flex flex-wrap gap-2">
            {SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                aria-label={`Colour ${swatch}`}
                onClick={() => setDraft({ ...draft, color: swatch })}
                className={`size-9 rounded-full border-2 ${
                  draft.color === swatch ? 'border-graphite' : 'border-transparent'
                }`}
                style={{ background: swatch }}
              />
            ))}
          </div>
        </Field>

        <Field label="Notes">
          <textarea
            className="field"
            value={draft.notes ?? ''}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="Gate code, parking, site contact."
          />
        </Field>

        {!isNew && (
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="size-5 accent-[var(--wm-brand-deep)]"
              checked={draft.active ?? true}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            <span className="text-sm">Active — shows in the crew's site picker</span>
          </label>
        )}

        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}
